import { useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Receipt,
  ExternalLink,
  Archive,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { formatHuf } from "@/components/SalaryFormSheet";
import { formatDateHu as formatDedDateHu } from "@/components/DeductionFormDialog";
import { Th, Td, openDeductionFile } from "./tabHelpers";
import { EmploymentDetailsSection } from "./EmploymentDetailsSection";
import { BenefitsSection } from "./BenefitsSection";
import { EfoRegistrationsTab } from "./EfoRegistrationsTab";

export function EmploymentTab({
  employee,
  onEmployeeChange,
  deductions,
  loading,
  onCreate,
  onDeductionChanged,
}) {
  const isEfo = employee?.employment_type === "efo";
  const [section, setSection] = useState("employment");

  return (
    <div className="space-y-6">
      <Tabs
        value={section}
        onValueChange={setSection}
        data-testid="employment-subtabs"
      >
        <TabsList className="bg-muted/40">
          <TabsTrigger
            value="employment"
            data-testid="employment-subtab-employment"
          >
            Munkaviszony
          </TabsTrigger>
          {isEfo && (
            <TabsTrigger
              value="registrations"
              data-testid="employment-subtab-registrations"
            >
              EFO bejelentések
            </TabsTrigger>
          )}
          <TabsTrigger
            value="benefits"
            data-testid="employment-subtab-benefits"
          >
            Adókedvezmények
          </TabsTrigger>
          <TabsTrigger
            value="deductions"
            data-testid="employment-subtab-deductions"
          >
            Bért érintő levonások
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employment" className="mt-5">
          {employee && (
            <EmploymentDetailsSection
              employee={employee}
              onEmployeeChange={onEmployeeChange}
            />
          )}
        </TabsContent>

        {isEfo && (
          <TabsContent value="registrations" className="mt-5">
            <EfoRegistrationsTab employee={employee} />
          </TabsContent>
        )}

        <TabsContent value="benefits" className="mt-5">
          <BenefitsSection employeeId={employee._id} />
        </TabsContent>

        <TabsContent value="deductions" className="mt-5">
          <DeductionsSection
            deductions={deductions}
            loading={loading}
            onCreate={onCreate}
            onDeductionChanged={onDeductionChanged}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DeductionsSection({ deductions, loading, onCreate, onDeductionChanged }) {
  const [deactivating, setDeactivating] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const total = useMemo(
    () => deductions.reduce((acc, d) => acc + Number(d.amount || 0), 0),
    [deductions]
  );
  const activeCount = useMemo(
    () => deductions.filter((d) => (d.status || "active") === "active").length,
    [deductions]
  );
  const inactiveCount = deductions.length - activeCount;
  const visible = useMemo(
    () =>
      showInactive
        ? deductions
        : deductions.filter((d) => (d.status || "active") === "active"),
    [deductions, showInactive]
  );

  return (
    <section
      data-testid="deductions-section"
      className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]"
    >
      <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Bért érintő levonások
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {deductions.length === 0
              ? "Még nincs rögzített levonás."
              : `${deductions.length} levonás (${activeCount} aktív) · ${formatHuf(total)} Ft összesen`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {inactiveCount > 0 && (
            <label
              htmlFor="deduction-show-inactive"
              className="flex cursor-pointer select-none items-center gap-2 text-[11.5px] font-medium text-muted-foreground"
              data-testid="deduction-show-inactive-label"
            >
              <Switch
                id="deduction-show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
                data-testid="deduction-show-inactive-switch"
              />
              Inaktívak ({inactiveCount})
            </label>
          )}
          <Button size="sm" data-testid="new-deduction-button" onClick={onCreate}>
            <Plus className="h-4 w-4" />
            Új levonás
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Levonások betöltése…
        </div>
      ) : deductions.length === 0 ? (
        <div
          data-testid="deductions-empty-state"
          className="flex flex-col items-center gap-3 px-6 py-14 text-center"
        >
          <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
            <Receipt className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
          <p className="text-sm font-medium">Nincs rögzített levonás</p>
          <p className="text-xs text-muted-foreground">
            Vidd fel a bért érintő levonásokat (NAV, bírósági letiltás, stb.).
          </p>
          <Button variant="outline" size="sm" className="mt-2 h-8" onClick={onCreate}>
            <Plus className="h-3.5 w-3.5" /> Új levonás
          </Button>
        </div>
      ) : visible.length === 0 ? (
        <div
          data-testid="deductions-all-inactive-state"
          className="flex flex-col items-center gap-2 px-6 py-12 text-center"
        >
          <p className="text-sm font-medium">Nincs aktív levonás</p>
          <p className="text-xs text-muted-foreground">
            {inactiveCount} inaktív levonás van — kapcsold be a fenti kapcsolóval, hogy lásd őket.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="deductions-table">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>Kérelmező</Th>
                <Th>Esedékesség kezdete</Th>
                <Th className="text-right">Összeg (Ft)</Th>
                <Th>Státusz</Th>
                <Th>Dokumentum</Th>
                <Th className="pr-4 text-right">Műveletek</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((d) => {
                const status = d.status || "active";
                return (
                <tr
                  key={d._id}
                  data-testid={`deduction-row-${d._id}`}
                  onClick={() => setViewing(d)}
                  className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                >
                  <Td>
                    <span className="font-medium">{d.requester}</span>
                    {d.notes && (
                      <p className="mt-0.5 truncate text-[12px] text-muted-foreground">
                        {d.notes}
                      </p>
                    )}
                    {status === "expired" && d.deactivation_reason && (
                      <p
                        className="mt-1 truncate text-[11px] italic text-muted-foreground/80"
                        title={d.deactivation_reason}
                      >
                        Indok: {d.deactivation_reason}
                      </p>
                    )}
                  </Td>
                  <Td className="text-muted-foreground">
                    {formatDedDateHu(d.due_start)}
                  </Td>
                  <Td className="text-right font-mono text-[12.5px]">
                    {formatHuf(d.amount)}
                  </Td>
                  <Td>
                    <DeductionStatusBadge value={status} />
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    {d.file_url ? (
                      <a
                        href="#"
                        data-testid={`deduction-file-link-${d._id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          openDeductionFile(d);
                        }}
                        className="inline-flex items-center gap-1.5 text-[12.5px] hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                        {d.file_name || "megnyitás"}
                      </a>
                    ) : (
                      <span className="text-[12px] text-muted-foreground/70">—</span>
                    )}
                  </Td>
                  <Td
                    className="whitespace-nowrap pr-4 text-right align-middle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {status === "active" ? (
                      <button
                        type="button"
                        onClick={() => setDeactivating(d)}
                        data-testid={`deduction-deactivate-${d._id}`}
                        className="inline-flex h-7 items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 text-[11.5px] font-medium leading-none text-amber-800 transition-colors hover:bg-amber-100 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                      >
                        <Archive className="h-3 w-3" strokeWidth={2} />
                        Inaktivál
                      </button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DeactivateDeductionDialog
        deduction={deactivating}
        onOpenChange={(open) => {
          if (!open) setDeactivating(null);
        }}
        onSuccess={(saved) => {
          setDeactivating(null);
          if (onDeductionChanged) onDeductionChanged(saved);
        }}
      />

      <DeductionDetailDialog
        deduction={viewing}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        onDeactivate={() => {
          const d = viewing;
          setViewing(null);
          setDeactivating(d);
        }}
      />
    </section>
  );
}

function DeductionStatusBadge({ value }) {
  const isActive = value === "active";
  return (
    <span
      data-testid={`deduction-status-${value}`}
      className={
        isActive
          ? "inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
          : "inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-500"
      }
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isActive ? "bg-emerald-500" : "bg-slate-400"
        }`}
      />
      {isActive ? "Aktív" : "Lejárt"}
    </span>
  );
}

function DeactivateDeductionDialog({ deduction, onOpenChange, onSuccess }) {
  const open = Boolean(deduction);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Reset state on each open
  useMemo(() => {
    if (open) {
      setReason("");
      setErr("");
    }
  }, [open, deduction?._id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (reason.trim().length < 3) {
      setErr("Az indoklás legalább 3 karakter hosszú legyen.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/deductions/${deduction._id}/deactivate`,
        { reason: reason.trim() }
      );
      toast.success("Levonás inaktiválva.");
      if (onSuccess) onSuccess(data);
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) ||
          "Nem sikerült az inaktiválás."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="deduction-deactivate-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-amber-100 text-amber-800">
              <Archive className="h-3.5 w-3.5" strokeWidth={2} />
            </span>
            Levonás inaktiválása
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">
              {deduction?.requester}
            </span>{" "}
            · {formatHuf(deduction?.amount)} Ft. <br />
            Az inaktiválás véglegesíti a levonás lezárását — utána már nem módosítható.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Indoklás *
            </span>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Pl. NAV visszavonta a letiltást, lejárt az időtartam, stb."
              data-testid="deduction-deactivate-reason"
              autoFocus
            />
          </label>
          {err && (
            <div
              data-testid="deduction-deactivate-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="deduction-deactivate-cancel"
            >
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={saving || reason.trim().length < 3}
              data-testid="deduction-deactivate-submit"
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Folyamatban…
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4" /> Inaktivál
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeductionDetailDialog({ deduction, onOpenChange, onDeactivate }) {
  const open = Boolean(deduction);
  if (!open) {
    return (
      <Dialog open={false} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }
  const status = deduction.status || "active";
  const isActive = status === "active";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="deduction-detail-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-foreground/5 text-foreground">
              <Receipt className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
            Levonás részletei
          </DialogTitle>
          <DialogDescription>
            A létrehozás után rögzített adatok — szerkesztés nem lehetséges.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <DetailField label="Kérelmező" value={deduction.requester} />
          <div className="grid grid-cols-2 gap-3">
            <DetailField
              label="Esedékesség kezdete"
              value={formatDedDateHu(deduction.due_start)}
            />
            <DetailField
              label="Összeg"
              value={`${formatHuf(deduction.amount)} Ft`}
              mono
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Státusz
            </p>
            <DeductionStatusBadge value={status} />
          </div>
          {deduction.notes && (
            <DetailField label="Megjegyzés" value={deduction.notes} multiline />
          )}
          {deduction.file_url && (
            <div className="space-y-1.5">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Dokumentum
              </p>
              <button
                type="button"
                onClick={() => openDeductionFile(deduction)}
                data-testid="deduction-detail-file-link"
                className="inline-flex items-center gap-1.5 text-[12.5px] text-foreground hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                {deduction.file_name || "Fájl megnyitása"}
              </button>
            </div>
          )}

          {!isActive && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-amber-800">
                Inaktiválás
              </p>
              {deduction.deactivated_at && (
                <p className="mt-1 text-[12px] text-amber-900/80">
                  {formatDedDateHu(deduction.deactivated_at)}
                  {deduction.deactivated_by_name && (
                    <> · {deduction.deactivated_by_name}</>
                  )}
                </p>
              )}
              {deduction.deactivation_reason && (
                <p className="mt-1 whitespace-pre-line text-[12.5px] text-amber-950">
                  {deduction.deactivation_reason}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="deduction-detail-close"
          >
            Bezár
          </Button>
          {isActive && (
            <Button
              type="button"
              onClick={onDeactivate}
              data-testid="deduction-detail-deactivate"
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              <Archive className="h-4 w-4" /> Inaktivál
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, value, mono, multiline }) {
  return (
    <div className="space-y-0.5">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p
        className={
          (mono ? "font-mono " : "") +
          (multiline ? "whitespace-pre-line " : "") +
          "text-[13px] text-foreground"
        }
      >
        {value || "—"}
      </p>
    </div>
  );
}
